import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

interface Item {
  id: number;
  name: string;
  itemNumber: string;
  // Additional fields from the database
  ItemID?: number;
  ItemName?: string;
  ItemCode?: string;
  CategoryID?: number;
  Description?: string;
  IsActive?: boolean;
  SellingPrice?: number;
  PurchasePrice?: number;
}

interface InventoryTransaction {
  id: number;
  itemId: number;
  transactionType: string;
  quantityChange: number;
  costPerUnit: number;
  transactionDate: string;
  referenceID?: string;
  documentNumber?: string;
  itemName?: string;
  itemNumber?: string;
}

interface ProfitLossData {
  dateRange: {
    start: Date;
    end: Date;
  };
  revenue: number;
  costOfGoodsSold: number;
  grossProfit: number;
  operatingExpenses: number;
  netProfit: number;
  expenses: {
    category: string;
    amount: number;
  }[];
  cogsDetails: any[];
  loading: boolean;

  // New fields for Profit/Loss Analysis
  moneyIn: number;
  moneyOut: number;
  netCashFlow: number;
  transactionDetails: {
    date: string;
    description: string;
    transactionType: string;
    type: 'IN' | 'OUT';
    amount: number;
    reference: string;
    category: string;
    itemName: string;
    itemNumber: string;
  }[];
}

@Component({
  selector: 'app-reports-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, CurrencyPipe],
  templateUrl: './reports-dashboard.component.html',
  styleUrl: './reports-dashboard.component.css'
})
export class ReportsDashboardComponent implements OnInit {
  today = new Date(); // For date picker max date

  reportData: ProfitLossData = {
    dateRange: {
      start: new Date(new Date().getFullYear(), 0, 1), // Start of current year
      end: new Date() // Today
    },
    revenue: 0,
    costOfGoodsSold: 0,
    grossProfit: 0,
    operatingExpenses: 0,
    netProfit: 0,
    expenses: [],
    cogsDetails: [],
    loading: false,
    moneyIn: 0,
    moneyOut: 0,
    netCashFlow: 0,
    transactionDetails: []
  };

  private apiBaseUrl = environment.apiUrl; // Use environment configuration

  constructor(private http: HttpClient) {}

  loading = false;
  error: string | null = null;

  ngOnInit() {
    this.loadReportData();
  }

  private calculateProfitLoss(transactions: any[], items: Item[] = []): void {
    // Reset values
    this.reportData.moneyIn = 0;
    this.reportData.moneyOut = 0;
    this.reportData.transactionDetails = [];

    // Create a map of items by ID for quick lookup
    const itemsMap = new Map<number, Item>();
    items.forEach(item => {
      // Handle different ID field names
      const itemId = item.ItemID || item.id;
      if (itemId) {
        itemsMap.set(itemId, item);
// $&
      } else {
// $&
      }
    });

    if (itemsMap.size === 0) {
// $&
    } else {
// $&
    }

    // Define transaction type mappings
    const incomeTypes = ['Sale', 'Production', 'Receipt'];
    const expenseTypes = ['Production Consumption', 'Adjustment'];
// $&

    // Process each transaction
// $&
    transactions.forEach((tx, index) => {
// $&

      const amount = Math.abs(tx.quantityChange * (tx.costPerUnit || 0));
      let type: 'IN' | 'OUT' = 'OUT';
      let category = 'Other';

      // Get item details - check both itemId and itemID properties
      const itemId = tx.itemId || tx.itemID || tx.ItemID;
      const item = itemsMap.get(itemId);

      // Get item name - check all possible property names
      const itemName = item ?
        (item.name || item.ItemName || 'Unknown Item') :
        'Unknown Item';

      // Get item number - check all possible property names
      const itemNumber = item ?
        (item.itemNumber || item.ItemCode || 'N/A') :
        'N/A';
// $&

      // Map transaction types to categories
      if (incomeTypes.includes(tx.transactionType)) {
        // For income, we need positive quantity change
        if (tx.quantityChange > 0) {
          this.reportData.moneyIn += amount;
          type = 'IN';
          category = 'Income';
        } else {
          this.reportData.moneyOut += amount;
          category = 'COGS';
        }
      } else if (expenseTypes.includes(tx.transactionType)) {
        this.reportData.moneyOut += amount;
        category = tx.transactionType === 'Production Consumption' ? 'COGS' : 'Expense';
      }

      // Only add to details if it has a financial impact
      if (amount > 0) {
        this.reportData.transactionDetails.push({
          date: tx.transactionDate,
          description: `${tx.transactionType} - ${itemName} (${itemNumber})`,
          transactionType: tx.transactionType,
          type,
          amount: type === 'IN' ? amount : -amount, // Show negative for expenses
          reference: tx.referenceID || 'N/A',
          category,
          itemName,
          itemNumber
        });
      }
    });

    // Sort transactions by date
    this.reportData.transactionDetails.sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Calculate net cash flow
    this.reportData.netCashFlow = this.reportData.moneyIn - this.reportData.moneyOut;
  }

  loadReportData() {
    this.loading = true;
    this.error = null;
    this.reportData.loading = true;

    const formatDate = (date: Date): string => {
      return date.toISOString().split('T')[0];
    };

    const startDate = formatDate(this.reportData.dateRange.start);
    const endDate = formatDate(this.reportData.dateRange.end);

    // Only fetch essential data
    forkJoin({
      transactions: this.getInventoryTransactions(startDate, endDate).pipe(
        tap(transactions => console.log('Fetched transactions:', transactions.length))
      ),
      items: this.getItems().pipe(
        tap(items => console.log('Fetched items:', items.length))
      )
    }).pipe(
      map(({ transactions, items }) => {
        // Calculate Profit/Loss from transactions with item details
        this.calculateProfitLoss(transactions, items);

        // Calculate basic financials
        const revenue = this.reportData.moneyIn;
        const costOfGoodsSold = this.reportData.moneyOut;
        const operatingExpenses = 0; // Not implemented yet
        const grossProfit = revenue - costOfGoodsSold;
        const netProfit = grossProfit - operatingExpenses;

        // PDF export functionality can be implemented here when needed

        // Update report data
        this.reportData = {
          ...this.reportData,
          revenue,
          costOfGoodsSold,
          grossProfit,
          operatingExpenses,
          netProfit,
          loading: false
        };

        return this.reportData;
      }),
      catchError(error => {
// $&
        this.error = 'Failed to load report data. Please try again.';
        this.reportData.loading = false;
        this.loading = false;
        return of(null);
      })
    ).subscribe(() => {
      this.loading = false;
    });
  }

  private getInventoryTransactions(startDate: string, endDate: string): Observable<InventoryTransaction[]> {
// $&
    return this.http.get<InventoryTransaction[]>(
      `${this.apiBaseUrl}/api/InventoryTransactions/GetAll`,
      { params: { startDate, endDate } }
    ).pipe(
      tap((transactions: InventoryTransaction[]) => {
// $&
      }),
      catchError(error => {
// $&
        return of([] as InventoryTransaction[]);
      })
    );
  }

  private getItems(): Observable<Item[]> {
    return this.http.get<any[]>(`${this.apiBaseUrl}/api/Items/GetAll`).pipe(
      map((items: any[]) => {
// $&
        return items.map(item => {
          const mappedItem: Item = {
            id: item.ItemID || item.id,
            name: item.ItemName || item.name || 'Unnamed Item',
            itemNumber: item.ItemCode || item.itemNumber || 'N/A',
            // Map all possible field variations
            ItemID: item.ItemID || item.id,
            ItemName: item.ItemName || item.name || 'Unnamed Item',
            ItemCode: item.ItemCode || item.itemNumber || 'N/A',
            CategoryID: item.CategoryID || item.categoryId,
            Description: item.Description || item.description,
            IsActive: item.IsActive !== undefined ? item.IsActive : (item.isActive !== undefined ? item.isActive : true),
            SellingPrice: item.SellingPrice || item.sellingPrice || 0,
            PurchasePrice: item.PurchasePrice || item.purchasePrice || 0
          };
// $&
          return mappedItem;
        });
      }),
      tap(items => console.log('Mapped items:', items)),
      catchError((error) => {
// $&
        return of([] as Item[]);
      })
    );
  }

  private calculateBasicCogs(transactions: any[]): number {
    if (!transactions?.length) return 0;

    // Sum up all outgoing inventory transactions
    return transactions
      .filter((tx: any) => tx.quantityChange < 0) // Only count outgoing transactions
      .reduce((sum: number, tx: any) => {
        const quantity = Math.abs(tx.quantityChange);
        const unitCost = tx.costPerUnit || 0;
        return sum + (quantity * unitCost);
      }, 0);
  }

  // Handle date range changes
  onDateRangeChange(event: any) {
    if (event?.startDate && event?.endDate) {
      this.reportData.dateRange = {
        start: new Date(event.startDate),
        end: new Date(event.endDate)
      };
      this.loadReportData();
    }
  }

  calculateGrossMargin(): number {
    if (this.reportData.revenue === 0) return 0;
    return (this.reportData.grossProfit / this.reportData.revenue) * 100;
  }

  calculateNetMargin(): number {
    if (this.reportData.revenue === 0) return 0;
    return (this.reportData.netProfit / this.reportData.revenue) * 100;
  }

  exportToPdf() {
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then((x) => {
        const doc = new jsPDF.default('p', 'pt', 'a4');
        const title = 'Profit/Loss Analysis Report';
        const dateRange = `${this.reportData.dateRange.start.toDateString()} to ${this.reportData.dateRange.end.toDateString()}`;

        // Add title
        doc.setFontSize(18);
        doc.text(title, 40, 40);
        doc.setFontSize(12);
        doc.text(`Date Range: ${dateRange}`, 40, 60);

        // Add summary
        const summaryY = 100;
        doc.setFontSize(14);
        doc.text('Summary', 40, summaryY);
        doc.setFontSize(12);
        doc.text(`Total Money In: ${this.reportData.moneyIn.toFixed(2)}`, 60, summaryY + 30);
        doc.text(`Total Money Out: ${this.reportData.moneyOut.toFixed(2)}`, 60, summaryY + 50);
        doc.text(`Net Cash Flow: ${this.reportData.netCashFlow.toFixed(2)}`, 60, summaryY + 70);

        // Add transaction table
        const tableColumn = ['Date', 'Description', 'Type', 'Amount', 'Reference'];
        const tableRows = this.reportData.transactionDetails.map(tx => [
          new Date(tx.date).toLocaleDateString(),
          tx.description,
          tx.type,
          tx.amount.toFixed(2),
          tx.reference
        ]);

        (doc as any).autoTable({
          head: [tableColumn],
          body: tableRows,
          startY: summaryY + 100,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [41, 128, 185] }
        });

        // Save the PDF
        doc.save(`profit-loss-report-${new Date().toISOString().split('T')[0]}.pdf`);
      });
    });
  }
}
